import * as React from 'react';
import { v4 } from 'uuid';
import type { FormInput } from './Form';
import { FormContext } from './Form';

export interface Validator<T> {
  validation(value: T): boolean;
  message?: string;
}

type InputValidation<T> = [React.RefObject<T>, boolean, string];
type Types<T> = T & (number | boolean | string | undefined | string[]);

type InputElements = HTMLElement & HTMLInputElement;

/**
 *
 * @param value Value to validate
 * @param validators array of validators that will be run for the value
 * @param deps Rerender dependencies for the validators, will update the validators when changed
 * @param validationGroup used to group multiple input validations together
 */
export const useInputValidation = <
  T = number | boolean | string,
  TRef extends InputElements = HTMLInputElement,
>(
  value: Types<T>,
  validators: Validator<T>[],
  deps: any[] = [],
  validationGroup?: string,
): InputValidation<TRef> => {
  const validationContext = React.useContext(FormContext);

  const [isValid, setIsValid] = React.useState<boolean | undefined>(undefined);
  const [isFocused, setIsFocused] = React.useState<undefined | boolean>(
    undefined,
  );
  const [isDirty, setIsDirty] = React.useState(false);
  const [message, setMessage] = React.useState<string | undefined>(undefined);
  const [inputId] = React.useState<string>(v4());
  const [contextInput, setContextInput] = React.useState<FormInput | undefined>(
    undefined,
  );
  const ref = React.useRef<InputElements>(null);

  // Create a memoized callback for the validators, prevents rerendering
  const cb = React.useCallback(() => validators, deps);

  const setFocus = React.useCallback(() => {
    ref.current?.focus();
  }, [ref]);

  /* On mount and unmount we register the input with the form contet */
  React.useEffect(() => {
    validationContext.addInput({
      value,
      validationGroup,
      validators: cb(),
      name: inputId,
      isValid: true,
      onFocus: setFocus,
    });

    const onBlur = () => {
      setIsFocused(false);
    };

    const onFocus = () => {
      setIsFocused(true);
    };

    // © Trúðalestin™ - 2020 make Landsbankinn great again

    ref.current?.addEventListener('blur', onBlur);
    ref.current?.addEventListener('focus', onFocus);

    return () => {
      validationContext.removeInput(inputId);

      ref.current?.removeEventListener('blur', onBlur);
      ref.current?.removeEventListener('focus', onFocus);
    };
  }, []);

  /* Update the current input when value or validators change */
  React.useEffect(() => {
    const { isValid, newMessage } = validate();

    setIsValid(isValid);
    if (!isFocused || validationContext.hasSubmitted) {
      setMessage(newMessage);
    }

    validationContext.updateInput({
      validationGroup,
      value,
      isValid,
      validators: cb(),
      name: inputId,
      onFocus: setFocus,
    });
  }, [value, cb, isFocused, validationContext.hasSubmitted]);

  React.useEffect(() => {
    setContextInput(validationContext.inputs.find((x) => x.name === inputId));
  }, [validationContext.inputs, inputId]);

  function validate() {
    if ((value === 0 || value) && !isDirty) {
      setIsDirty(true);
    }
    if (!validationContext.hasSubmitted && !isDirty) {
      return { isValid: true };
    }

    let message;
    const isValid = validators.reduce((current, validator) => {
      if (!current) {
        return false;
      }

      if (!validator.validation(value)) {
        message = validator.message;

        return false;
      }
      return true;
    }, true);

    return {
      newMessage: message,
      isValid,
    };
  }

  const isContextInputValid =
    contextInput !== undefined ? contextInput.isValid : true;
  const contextMessage =
    contextInput !== undefined ? contextInput.invalidMessge : undefined;

  return [
    ref as React.RefObject<TRef>, // fugly hack
    isValid !== false && isContextInputValid,
    message || contextMessage || '',
  ];
};
